<template>
  <b-modal
    v-model="visible"
    :title="$t('retention.releaseTeam.modalTitle')"
    size="md"
    centered
    lazy
    hide-footer
  >
    <template v-if="retention">
      <p class="mb-1">
        {{ $t('retention.releaseTeam.modalIntro', { team: retention.team ? retention.team.name : '' }) }}
      </p>

      <div
        v-if="loading"
        class="text-center py-2"
      >
        <b-spinner small />
      </div>

      <template v-else>
        <div class="form-group">
          <label>{{ $t('retention.releaseTeam.positionLabel') }}</label>
          <select
            v-model="positionTypeId"
            class="form-control"
            :disabled="saving"
          >
            <option
              v-for="p in positions"
              :key="p.id"
              :value="p.id"
            >
              {{ p.code }} — {{ p.name }}{{ p.id === suggestedId ? ` (${$t('retention.releaseTeam.suggested')})` : '' }}
            </option>
            <option :value="null">
              {{ $t('retention.releaseTeam.noPosition') }}
            </option>
          </select>
          <small class="text-muted">{{ $t('retention.releaseTeam.suggestedHint') }}</small>
        </div>

        <div
          v-if="stillHeldBy.length"
          class="rrt__aviso"
        >
          <strong>{{ $t('retention.releaseTeam.stillHeldTitle') }}</strong>
          <div
            v-for="r in stillHeldBy"
            :key="r.id"
          >
            {{ $t('retention.releaseTeam.stillHeldItem', { equipment: r.equipment || '—', protocol: r.protocol || '—' }) }}
          </div>
          <small>{{ $t('retention.releaseTeam.stillHeldNote') }}</small>
        </div>

        <div
          class="d-flex justify-content-end mt-1"
          style="gap: 8px"
        >
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="saving"
            @click="visible = false"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-warning"
            :disabled="saving"
            @click="confirm"
          >
            {{ saving ? $t('retention.common.saving') : $t('retention.releaseTeam.confirmButton') }}
          </button>
        </div>
      </template>
    </template>
  </b-modal>
</template>

<script>
import swal from 'sweetalert'
import ApiErrorMixin from '@/mixins/ApiErrorMixin'

/**
 * "Liberar equipe": o operador escolhe a posição em que a equipe volta; a sugerida é a última que ela tinha antes
 * de ser presa pela retenção. A retenção segue aberta. Uso: `$refs.x.open(retencao)`; emite `released` ao concluir.
 */
export default {
  name: 'RetentionReleaseTeamModal',

  mixins: [ApiErrorMixin],

  data() {
    return {
      visible: false,
      retention: null,
      loading: false,
      saving: false,
      positions: [],
      suggestedId: null,
      stillHeldBy: [],
      positionTypeId: null,
    }
  },

  methods: {
    async open(retention) {
      this.retention = retention
      this.visible = true
      this.loading = true
      this.positions = []
      this.stillHeldBy = []
      this.positionTypeId = null

      try {
        const res = await this.$http.get(`${this.$store.state.api}equipment/retentions/${retention.id}/release-options`)
        const data = res.data.data || {}
        this.positions = data.positions || []
        this.stillHeldBy = data.still_held_by || []
        this.suggestedId = data.suggested_position_type_id || null
        // Sugerida só vale se está entre as oferecidas; senão começa em "sem posição".
        this.positionTypeId = this.positions.some(p => p.id === this.suggestedId) ? this.suggestedId : null
      } catch (e) {
        this.visible = false
        this.$message(this.$t('retention.common.errorTitle'), this.apiErrorText(e, this.$t('retention.releaseTeam.optionsError')), 'error')
      } finally {
        this.loading = false
      }
    },

    async confirm() {
      this.saving = true
      try {
        const res = await this.$http.post(
          `${this.$store.state.api}equipment/retentions/${this.retention.id}/release-team`,
          { position_type_id: this.positionTypeId },
        )
        const aindaPresa = (res.data.data && res.data.data.team_still_held_by) || []
        this.visible = false

        if (aindaPresa.length) {
          // A equipe só sai da posição de retenção quando nenhuma retenção a prende: a posição escolhida ainda não valeu.
          const lista = aindaPresa
            .map(r => this.$t('retention.releaseTeam.stillHeldItem', { equipment: r.equipment || '—', protocol: r.protocol || '—' }))
            .join('\n')
          swal({
            title: this.$t('retention.releaseTeam.stillHeldTitle'),
            text: this.$t('retention.releaseTeam.stillHeldText', { team: this.retention.team ? this.retention.team.name : '', list: lista }),
            icon: 'info',
          })
        } else {
          this.$message(this.$t('retention.common.successTitle'), this.$t('retention.releaseTeam.success'), 'success')
        }

        this.$emit('released')
      } catch (e) {
        this.$message(this.$t('retention.common.errorTitle'), this.apiErrorText(e, this.$t('retention.releaseTeam.error')), 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.rrt__aviso {
  margin-top: 8px;
  padding: 8px 12px;
  border: 1px dashed #ff9f43;
  border-radius: 6px;
  background: rgba(255, 159, 67, 0.08);
  font-size: 0.85rem;
}
</style>
